import * as _ from 'lodash';
import {
    isReadOnlyScope,
    isSandboxBrand,
    isSandboxJurisdiction,
    JurisdictionType
} from '../../shared/models/jurisdictions';
import { getPermissions } from '../../auth/login/redux/selectors';
import { compareIds } from '../../shared/helpers/id.helper';
import {
    canUpdateRiskConfig,
    canUpdateRules,
    hasPermission,
    holdsRiskConfigForSomeBrandsOnly,
    holdsRulesForSomeBrandsOnly,
    PermissionEnum
} from '../../shared/models/permissions';

export const getJurisdictionState = store => store && store.jurisdiction;

export const getBrandsAndJurisdictions = store =>
    getJurisdictionState(store).data;

export const getSelectedJurisdiction = store =>
    getJurisdictionState(store).selectedJurisdiction;

export const getSelectedBrand = store =>
    getJurisdictionState(store).selectedBrand;

export const getSelectedJurisdictionCode = store =>
    getSelectedJurisdiction(store) && getSelectedJurisdiction(store).code;

export const getSelectedBrandId = store =>
    getSelectedBrand(store) && getSelectedBrand(store).id;

/*
  The type of a jurisdiction, by its code, in the list from /general/brandsandjurisdictions. A code that is not in the list (such as
  ALL), or an entry with no type, is PRODUCTION: the type was added by the backend later.
*/
export const getJurisdictionTypeByCode = (store, code) =>
    _.get(
        _.find(
            _.get(getBrandsAndJurisdictions(store), 'jurisdictions', []),
            jurisdiction => !!code && jurisdiction.code === code
        ),
        'type',
        JurisdictionType.PRODUCTION
    );

/* The codes of the sandboxes (jurisdictions of type SIMULATION) that the logged in user may see. */
export const getSandboxJurisdictionCodes = store =>
    _.map(
        _.filter(
            _.get(getBrandsAndJurisdictions(store), 'jurisdictions', []),
            isSandboxJurisdiction
        ),
        'code'
    );

/* Whether what is selected at the top is a sandbox, or a brand of one (a run). */
export const isSandboxSelected = store =>
    isReadOnlyScope(getSelectedJurisdiction(store), getSelectedBrand(store));

const withoutSandboxes = _.memoize(
    list => ({
        ...list,
        jurisdictions: _.reject(list.jurisdictions, isSandboxJurisdiction),
        brands: _.reject(list.brands, isSandboxBrand)
    }),
    list => list
);

/* How many runs of a sandbox the selector lists. A sandbox keeps no more than this (backend issue 100), so it lists all of them. */
export const maxRunsListedPerSandbox = 5;

// Remembers the last result, for a function of references that must return the same object while they are the same.
const memoizeLast = fn => {
    let lastArgs = null;
    let lastResult;

    return (...args) => {
        if (!lastArgs || args.some((arg, index) => arg !== lastArgs[index])) {
            lastResult = fn(...args);
            lastArgs = args;
        }
        return lastResult;
    };
};

/*
  The list with only the newest runs of each sandbox (a run has a brand of its own in its sandbox, and they accumulate). The newest is
  the greater brand id: the ids are made by the backend with the time in their high bits. The brand that is selected stays, however
  old, because the runs page sends the user to one: a selection the selector does not list would show as another.
  The same list when nothing is left out, so that the selector does not render again for nothing.
*/
const withNewestRuns = memoizeLast((list, selectedBrandId) => {
    const brands = _.get(list, 'brands', []);
    const newest = new Set(
        _.map(
            _.flatMap(
                _.groupBy(_.filter(brands, isSandboxBrand), 'jurisdiction'),
                runs =>
                    _.take(
                        [...runs].sort((a, b) => compareIds(b.id, a.id)),
                        maxRunsListedPerSandbox
                    )
            ),
            'id'
        )
    );
    const selected = _.isNil(selectedBrandId) ? null : String(selectedBrandId);
    const listed = _.filter(
        brands,
        brand =>
            !isSandboxBrand(brand) ||
            newest.has(brand.id) ||
            String(brand.id) === selected
    );

    return listed.length === brands.length ? list : { ...list, brands: listed };
});

/*
  The jurisdictions and brands that the selector at the top lists. Until the backend only returns the sandboxes a user may see (it
  returns all of them today: backend issue 30), the sandboxes are only listed to whoever manages the simulation, so that nobody sees
  the sandboxes of a team they are not in; and a sandbox lists only its newest runs. The team editor reads the whole list, not this one.
*/
export const getSelectableJurisdictionsAndBrands = store => {
    const list = getBrandsAndJurisdictions(store);

    if (!list) {
        return list;
    }
    return hasPermission(
        getPermissions(store),
        PermissionEnum.MANAGE_SIMULATIONS
    )
        ? withNewestRuns(list, getSelectedBrandId(store))
        : withoutSandboxes(list);
};

/* The ids of the brands the jurisdiction has, in the list from /general/brandsandjurisdictions (which lists every brand of a production jurisdiction). */
export const getBrandIdsOfJurisdiction = (store, code) =>
    _.map(
        _.filter(
            _.get(getBrandsAndJurisdictions(store), 'brands', []),
            brand => !!code && brand.jurisdiction === code
        ),
        'id'
    );

/* Whether the user may change the rules of the category in the jurisdiction that is selected, as the server decides it: with the permission for every brand of it. */
export const getCanUpdateRules = (store, ruleCategory) => {
    const code = getSelectedJurisdictionCode(store);

    return canUpdateRules(
        getPermissions(store),
        ruleCategory,
        code,
        getJurisdictionTypeByCode(store, code),
        getBrandIdsOfJurisdiction(store, code)
    );
};

/* Whether the user may change the risk configuration of the jurisdiction that is selected, as the server decides it: with the permission for every brand of it. */
export const getCanUpdateRiskConfig = store => {
    const code = getSelectedJurisdictionCode(store);

    return canUpdateRiskConfig(
        getPermissions(store),
        code,
        getJurisdictionTypeByCode(store, code),
        getBrandIdsOfJurisdiction(store, code)
    );
};

/* Whether the user holds the permission to change these rules for some brands of the jurisdiction that is selected, and not all: they may not. */
export const getHoldsRulesForSomeBrandsOnly = (store, ruleCategory) => {
    const code = getSelectedJurisdictionCode(store);

    return holdsRulesForSomeBrandsOnly(
        getPermissions(store),
        ruleCategory,
        code,
        getJurisdictionTypeByCode(store, code),
        getBrandIdsOfJurisdiction(store, code)
    );
};

/* Whether the user holds the permission to change the risk configuration for some brands of the jurisdiction that is selected, and not all. */
export const getHoldsRiskConfigForSomeBrandsOnly = store => {
    const code = getSelectedJurisdictionCode(store);

    return holdsRiskConfigForSomeBrandsOnly(
        getPermissions(store),
        code,
        getJurisdictionTypeByCode(store, code),
        getBrandIdsOfJurisdiction(store, code)
    );
};
